/**
 * Correct the name and email on a reservation (BON-472).
 *
 * Contact only. Seats, workshop and price are locked on a hold — changing any of
 * them is a capacity claim, which is release-and-recreate — and the footer says
 * so rather than leaving a CSR to discover it.
 *
 * SAVE IS NOT GATED ON WHETHER ANYTHING CHANGED, and this modal never claims a
 * rotation is about to happen. The server compares trimmed and lowercased on both
 * sides and reports what it did in `credentialRotated`; a browser-side comparison
 * would warn about — or refuse — a case-only retype that rotates nothing, because
 * stored addresses are not reliably lowercase.
 *
 * THE WARNING IS THE DIALOG'S DESCRIPTION, not decoration beside it: labelled
 * alone, a screen reader announces the title and stops, and the consequence of
 * changing the address is never read out.
 */
import { useRef } from 'react';

import type { ReservationRow } from '../../api/reservations';
import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { DialogFooterActions } from '../../shared/DialogFooterActions';

import { ReservationContactFields } from './ReservationContactFields';
import type { RotationOutcome } from './reservationRotationOutcome';
import { useReservationContactForm } from './useReservationContactForm';

export function EditReservationContactDialog({
  reservation,
  onClose,
  onDone,
  onRefresh,
}: {
  reservation: ReservationRow;
  onClose: () => void;
  /** Fired when the save SUCCEEDED — the caller owns the toast, the alert and
   *  the list refresh. Failures never reach it: they stay in the form. */
  onDone: (outcome: RotationOutcome) => void;
  /** Re-read the list WITHOUT closing this form, for a failure that means the
   *  row behind it is already out of date. */
  onRefresh: () => void;
}) {
  const form = useReservationContactForm(reservation, onDone, onRefresh);
  const nameRef = useRef<HTMLInputElement>(null);
  useDialogKeyboard(onClose, nameRef, form.saving);

  // Escape, Cancel and the backdrop all go inert while the save is out. None of
  // them can call it back, and a CSR who thinks they cancelled a save that then
  // succeeds has already told the customer the wrong thing.
  const dismiss = () => {
    if (!form.saving) onClose();
  };

  return (
    <div
      role="presentation"
      onClick={dismiss}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={form.submit}
        // The fields keep `required` for assistive tech, but the browser's own
        // validation is switched off: its bubble is unstyled, untranslatable and
        // disappears on the next click, and it suppresses the submit entirely so
        // the form's own message never gets a chance to say the same thing in the
        // same place as every other error here.
        noValidate
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-reservation-contact-title"
        aria-describedby="edit-reservation-contact-warning"
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1a1f1c]"
      >
        <div className="border-b border-stone-200 px-6 py-4 dark:border-stone-700">
          <h2
            id="edit-reservation-contact-title"
            className="text-lg font-bold text-bark-900 dark:text-stone-100"
          >
            Edit reservation contact
          </h2>
          <p className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
            {reservation.reference} · {reservation.seats}{' '}
            {reservation.seats === 1 ? 'seat' : 'seats'} held on {reservation.workshopName}
          </p>
        </div>

        <ReservationContactFields
          name={form.name}
          email={form.email}
          onNameChange={form.setName}
          onEmailChange={form.setEmail}
          error={form.error}
          nameRef={nameRef}
        />

        <DialogFooterActions
          note="Seats, workshop and price are locked on a hold."
          onCancel={dismiss}
          confirmLabel="Save"
          confirmBusyLabel="Saving…"
          busy={form.saving}
        />
      </form>
    </div>
  );
}
